import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  UserCheck,
  UserX,
  DollarSign,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import AdjustBalanceModal from '../admin/AdjustBalanceModal';

const ClientsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAdjustBalanceModal, setShowAdjustBalanceModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: clientsData, isLoading, error } = useQuery(
    ['clients', { page: currentPage, search: searchTerm, sortBy, sortOrder }],
    () => api.get('/users', {
      params: {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        sortBy,
        sortOrder
      }
    }).then(res => res.data),
    {
      keepPreviousData: true,
      staleTime: 30000
    }
  );

  const deleteMutation = useMutation(
    (clientId) => api.delete(`/users/${clientId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients');
        toast.success('Cliente desactivado exitosamente');
        setShowDeleteModal(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al desactivar cliente');
      }
    }
  );

  const toggleStatusMutation = useMutation(
    ({ clientId, isActive }) => api.put(`/users/${clientId}`, { isActive: !isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients');
        toast.success('Estado del cliente actualizado');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar estado');
      }
    }
  );

  const handleDeleteClient = (clientId) => {
    deleteMutation.mutate(clientId);
  };

  const handleToggleStatus = (client) => {
    toggleStatusMutation.mutate({ clientId: client.id, isActive: client.is_active });
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const calculateROI = (netWinnings, initialCapital) => {
    if (!initialCapital || initialCapital === 0) return 0;
    return ((netWinnings / initialCapital) * 100).toFixed(1);
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300">
        <UserCheck className="w-3 h-3 mr-1" />
        Activo
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
        <UserX className="w-3 h-3 mr-1" />
        Inactivo
      </span>
    );
  };

  const DropdownMenu = ({ client, onClose, onAdjustBalance }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      className="absolute right-0 top-8 w-48 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 py-1 z-20"
    >
      <Link
        to={`/admin/clients/${client.id}`}
        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
        onClick={onClose}
      >
        <Eye className="w-4 h-4 mr-3" />
        Ver detalles
      </Link>

      <Link
        to={`/admin/clients/${client.id}/edit`}
        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
        onClick={onClose}
      >
        <Edit className="w-4 h-4 mr-3" />
        Editar
      </Link>

      <button
        onClick={() => {
          onAdjustBalance?.(client);
          onClose();
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
      >
        <DollarSign className="w-4 h-4 mr-3" />
        Ajustar Saldo
      </button>

      <button
        onClick={() => {
          handleToggleStatus(client);
          onClose();
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
      >
        {client.is_active ? (
          <>
            <UserX className="w-4 h-4 mr-3" />
            Desactivar
          </>
        ) : (
          <>
            <UserCheck className="w-4 h-4 mr-3" />
            Activar
          </>
        )}
      </button>

      <hr className="my-1 border-gray-200 dark:border-dark-700" />

      <button
        onClick={() => {
          setShowDeleteModal(client);
          onClose();
        }}
        className="flex items-center w-full px-4 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20"
      >
        <Trash2 className="w-4 h-4 mr-3" />
        Eliminar
      </button>
    </motion.div>
  );

  const DeleteModal = ({ client, onClose, onConfirm }) => (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="w-full max-w-md bg-white dark:bg-dark-800 rounded-xl shadow-2xl p-6"
        >
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-danger-100 dark:bg-danger-900/30 rounded-full">
            <AlertCircle className="w-6 h-6 text-danger-600 dark:text-danger-400" />
          </div>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 text-center mb-2">
            ¿Desactivar cliente?
          </h3>

          <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
            ¿Estás seguro de que quieres desactivar a <strong>{client?.full_name}</strong>?
            Esta acción se puede revertir más tarde.
          </p>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancelar
            </button>
            <button
              onClick={() => onConfirm(client.id)}
              className="flex-1 btn-danger"
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="spinner w-4 h-4"></div>
                  <span>Desactivando...</span>
                </div>
              ) : (
                'Desactivar'
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error al cargar clientes
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {error.response?.data?.error || 'Error de conexión'}
        </p>
      </div>
    );
  }

  const { users = [], pagination = {} } = clientsData || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestión de Clientes
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Administra los clientes de la plataforma
          </p>
        </div>

        <Link
          to="/admin/clients/new"
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Cliente
        </Link>
      </div>

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Sort Options */}
          <div className="flex items-center space-x-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field"
            >
              <option value="created_at">Fecha de registro</option>
              <option value="full_name">Nombre</option>
              <option value="total_winnings">Ganancias</option>
              <option value="current_balance">Saldo</option>
            </select>

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="input-field"
            >
              <option value="desc">Descendente</option>
              <option value="asc">Ascendente</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
              <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Clientes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {pagination.totalItems || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-success-100 dark:bg-success-900/30 rounded-xl">
              <UserCheck className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Activos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {users.filter(u => u.is_active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-warning-100 dark:bg-warning-900/30 rounded-xl">
              <DollarSign className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Capital Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {formatCurrency(users.reduce((sum, u) => {
                  const balance = Number(u.current_balance) || 0;
                  return sum + balance;
                }, 0))}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-danger-100 dark:bg-danger-900/30 rounded-xl">
              <TrendingUp className="w-6 h-6 text-danger-600 dark:text-danger-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Ganancias Netas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {formatCurrency(users.reduce((sum, u) => {
                  const totalWinnings = Number(u.total_winnings) || 0;
                  const initialCapital = Number(u.initial_capital) || 0;
                  const currentBalance = Number(u.current_balance) || 0;
                  // Ganancia neta = Saldo actual - Capital inicial
                  const netProfit = currentBalance - initialCapital;
                  return sum + netProfit;
                }, 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <div className="card overflow-hidden">
        {users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No hay clientes
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm ? 'No se encontraron clientes con ese criterio de búsqueda' : 'Aún no has creado ningún cliente'}
            </p>
            {!searchTerm && (
              <Link to="/admin/clients/new" className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Crear primer cliente
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View - Hidden on mobile */}
            <div className="hidden md:block">
              {/* Table Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-700">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                  <div className="col-span-3">Cliente</div>
                  <div className="col-span-2">Estado</div>
                  <div className="col-span-2 text-right">Capital Inicial</div>
                  <div className="col-span-2 text-right">Saldo Actual</div>
                  <div className="col-span-2 text-right">Ganancias Netas</div>
                  <div className="col-span-1 text-center">Acciones</div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200 dark:divide-dark-700">
                {users.map((client, index) => {
                  const currentBalance = Number(client.current_balance) || 0;
                  const initialCapital = Number(client.initial_capital) || 0;
                  const netProfit = currentBalance - initialCapital;
                  const roi = calculateROI(netProfit, initialCapital);

                  return (
                    <motion.div
                      key={client.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
                    >
                      <div className="grid grid-cols-12 gap-4 items-center">
                        {/* Client Info */}
                        <div className="col-span-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {client.full_name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {client.full_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {client.email}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="col-span-2">
                          {getStatusBadge(client.is_active)}
                        </div>

                        {/* Initial Capital */}
                        <div className="col-span-2 text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatCurrency(client.initial_capital)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Semilla: {formatCurrency(client.seed_amount)}
                          </p>
                        </div>

                        {/* Current Balance */}
                        <div className="col-span-2 text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatCurrency(client.current_balance)}
                          </p>
                        </div>

                        {/* Net Winnings */}
                        <div className="col-span-2 text-right">
                          {(() => {
                            const currentBalance = Number(client.current_balance) || 0;
                            const initialCapital = Number(client.initial_capital) || 0;
                            const netProfit = currentBalance - initialCapital;

                            return (
                              <>
                                <p className={`text-sm font-medium ${
                                  netProfit > 0
                                    ? 'text-success-600 dark:text-success-400'
                                    : netProfit < 0
                                    ? 'text-danger-600 dark:text-danger-400'
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                  {formatCurrency(netProfit)}
                                </p>
                                <p className={`text-xs ${
                                  roi > 0
                                    ? 'text-success-600 dark:text-success-400'
                                    : roi < 0
                                    ? 'text-danger-600 dark:text-danger-400'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}>
                                  ROI: {roi}%
                                </p>
                              </>
                            );
                          })()}
                        </div>

                        {/* Actions */}
                        <div className="col-span-1 text-center relative">
                          <button
                            onClick={() => setActiveDropdown(activeDropdown === client.id ? null : client.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          <AnimatePresence>
                            {activeDropdown === client.id && (
                              <DropdownMenu
                                client={client}
                                onClose={() => setActiveDropdown(null)}
                                onAdjustBalance={(c) => {
                                  console.log('onAdjustBalance called with client:', c);
                                  setSelectedClient(c);
                                  setShowAdjustBalanceModal(true);
                                  console.log('Modal should now be open');
                                }}
                              />
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Mobile Card View - Visible only on mobile */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-dark-700">
              {users.map((client, index) => {
                const currentBalance = Number(client.current_balance) || 0;
                const initialCapital = Number(client.initial_capital) || 0;
                const netProfit = currentBalance - initialCapital;
                const roi = calculateROI(netProfit, initialCapital);

                return (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="w-10 h-10 flex-shrink-0 bg-gradient-primary rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {client.full_name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {client.full_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {client.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                        {getStatusBadge(client.is_active)}
                        <button
                          onClick={() => setActiveDropdown(activeDropdown === client.id ? null : client.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        <AnimatePresence>
                          {activeDropdown === client.id && (
                            <DropdownMenu
                              client={client}
                              onClose={() => setActiveDropdown(null)}
                              onAdjustBalance={(c) => {
                                console.log('onAdjustBalance called (mobile view) with client:', c);
                                setSelectedClient(c);
                                setShowAdjustBalanceModal(true);
                                console.log('Modal should now be open (mobile view)');
                              }}
                            />
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400 mb-1">Capital Inicial</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.initial_capital)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Semilla: {formatCurrency(client.seed_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400 mb-1">Saldo Actual</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.current_balance)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400 mb-1">Ganancias Netas</p>
                        <p className={`text-sm font-medium ${
                          netProfit > 0
                            ? 'text-success-600 dark:text-success-400'
                            : netProfit < 0
                            ? 'text-danger-600 dark:text-danger-400'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {formatCurrency(netProfit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400 mb-1">ROI</p>
                        <p className={`text-sm font-semibold ${
                          roi > 0
                            ? 'text-success-600 dark:text-success-400'
                            : roi < 0
                            ? 'text-danger-600 dark:text-danger-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {roi}%
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a{' '}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}{' '}
            de {pagination.totalItems} clientes
          </p>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>

            <span className="text-sm text-gray-600 dark:text-gray-400">
              Página {pagination.currentPage} de {pagination.totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
              disabled={currentPage === pagination.totalPages}
              className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <DeleteModal
          client={showDeleteModal}
          onClose={() => setShowDeleteModal(null)}
          onConfirm={handleDeleteClient}
        />
      )}

      {/* Click outside handler for dropdown */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setActiveDropdown(null)}
        />
      )}

      {/* Adjust Balance Modal */}
      <AdjustBalanceModal
        isOpen={showAdjustBalanceModal}
        onClose={() => {
          setShowAdjustBalanceModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onSuccess={() => {
          queryClient.invalidateQueries(['clients']);
        }}
      />
    </div>
  );
};

export default ClientsList;